
import React from 'react';

interface Product {
  name: string;
  longDescription: string;
  pricing: string;
  commission: string;
  rating: number;
  reviews: number;
  category: string;
  affiliateLink: string;
}

interface DealDescriptionProps {
  product: Product;
}

/**
 * DealDescription component renders detailed information about a product deal.
 * Includes robust handling for missing product data and safe URL linking.
 */
export default function DealDescription({ product }: DealDescriptionProps) {
  // Guard against missing product prop (should never happen with proper typing)
  if (!product) {
    return (
      <section className="px-6 lg:px-8 pb-16">
        <div className="max-w-7xl mx-auto text-white">
          <p>No product information available.</p>
        </div>
      </section>
    );
  }

  // Ensure affiliateLink is a safe URL; fallback to "#" if invalid
  const safeAffiliateLink = (() => {
    try {
      // Basic validation – must be a non‑empty string and a valid URL
      if (typeof product.affiliateLink === 'string' && product.affiliateLink.trim()) {
        // If the link does not include a protocol, prepend https://
        const url = product.affiliateLink.startsWith('http')
          ? product.affiliateLink
          : `https://${product.affiliateLink}`;
        // This will throw if the URL is malformed
        new URL(url);
        return url;
      }
    } catch {
      // Swallow error and fallback
    }
    return '#';
  })();

  return (
    <section className="px-6 lg:px-8 pb-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Description */}
          <div className="lg:col-span-2">
            <div className="bg-white/[0.03] border border-white/5 rounded-none p-8 lg:p-10">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                <div className="w-8 h-8 bg-neon/10 flex items-center justify-center rounded-none">
                  <i className="ri-file-text-line text-neon text-lg"></i>
                </div>
                About This Deal
              </h2>
              <p className="text-white/50 text-sm leading-[1.8]">
                {product.longDescription}
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="bg-white/[0.03] border border-white/5 rounded-none p-6">
              <h3 className="text-sm font-bold text-white mb-5 uppercase tracking-wider">
                Deal Summary
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-white/40 text-sm">Category</span>
                  <span className="text-white/70 text-sm font-medium">{product.category}</span>
                </div>
                <div className="border-t border-white/5"></div>
                <div className="flex items-center justify-between">
                  <span className="text-white/40 text-sm">Pricing</span>
                  <span className="text-neon text-sm font-bold">{product.pricing}</span>
                </div>
                <div className="border-t border-white/5"></div>
                <div className="flex items-center justify-between">
                  <span className="text-white/40 text-sm">Commission</span>
                  <span className="text-white/70 text-sm font-medium">{product.commission}</span>
                </div>
                <div className="border-t border-white/5"></div>
                <div className="flex items-center justify-between">
                  <span className="text-white/40 text-sm">Rating</span>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-amber-400 text-xs"></i>
                    <span className="text-white/70 text-sm font-medium">{product.rating}</span>
                  </div>
                </div>
                <div className="border-t border-white/5"></div>
                <div className="flex items-center justify-between">
                  <span className="text-white/40 text-sm">Reviews</span>
                  <span className="text-white/70 text-sm font-medium">
                    {product.reviews?.toLocaleString() ?? '0'}
                  </span>
                </div>
              </div>
            </div>

            {/* CTA Card */}
            <div className="bg-neon/5 border border-neon/20 rounded-none p-6 text-center">
              <div className="w-12 h-12 bg-neon/10 flex items-center justify-center mx-auto mb-4">
                <i className="ri-rocket-2-line text-neon text-xl"></i>
              </div>
              <h3 className="text-white font-bold text-base mb-2">Ready to get started?</h3>
              <p className="text-white/40 text-xs mb-5 leading-relaxed">
                Grab this deal before it expires and start saving today.
              </p>
              <a
                href={safeAffiliateLink}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="block w-full py-3 bg-neon text-black text-sm font-bold rounded-none hover:bg-neon/90 transition-colors whitespace-nowrap cursor-pointer"
              >
                Claim Deal <i className="ri-arrow-right-up-line ml-1"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
